import { useState, useRef, useEffect, useEffectEvent } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  capitalizeFirst,
  pluralPointsLabel,
  pointsLabel,
} from "@/lib/points-label";
import { Button } from "@/components/ui/button";
import { UseQueryResult } from "@tanstack/react-query";
import { CartResponse } from "@/networking/carts.api";
import { Input } from "@/components/ui/input/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FluidPointsProps {
  query: UseQueryResult<CartResponse | null>;
  onApplyPoints: (points: number, useForEntireSubtotal: boolean) => void;
  onRemovePoints: () => void;
  isLoading?: boolean;
  isRemoving?: boolean;
}

export default function FluidPoints({
  query,
  onApplyPoints,
  onRemovePoints,
  isLoading = false,
  isRemoving = false,
}: FluidPointsProps) {
  const { t } = useTranslation();
  const [accordionValue, setAccordionValue] = useState<string>("");
  const cart = query?.data?.cart;
  const company = cart?.company;
  const {
    customer_total_points: customerTotalPoints,
    customer_total_points_in_currency: customerTotalPointsInCurrency,
    currency_symbol: currencySymbol,
    points_applied_amount_in_currency: appliedPointsAmountInCurrency,
    points_applied: appliedPoints,
    max_applicable_points: maxApplicablePoints,
  } = cart ?? {};
  const [pointsToRedeem, setPointsToRedeem] = useState<number>(
    appliedPoints ?? 0,
  );
  const prevAppliedRef = useRef(appliedPoints);
  if (appliedPoints !== prevAppliedRef.current) {
    prevAppliedRef.current = appliedPoints;
    setPointsToRedeem(appliedPoints ?? 0);
  }
  const [useForEntireSubtotal, setUseForEntireSubtotal] = useState(false);
  const prevMaxRef = useRef(maxApplicablePoints);

  const onMaxPointsIncreased = useEffectEvent(() => {
    if (useForEntireSubtotal) {
      setUseForEntireSubtotal(false);
    }
  });

  useEffect(() => {
    const prevMax = prevMaxRef.current;

    if (
      maxApplicablePoints !== undefined &&
      prevMax !== undefined &&
      maxApplicablePoints > prevMax
    ) {
      onMaxPointsIncreased();
    }

    prevMaxRef.current = maxApplicablePoints;
  }, [maxApplicablePoints]);

  const handleCheckboxChange = (checked: boolean) => {
    setUseForEntireSubtotal(checked);
    if (checked && maxApplicablePoints && maxApplicablePoints > 0) {
      setPointsToRedeem(maxApplicablePoints);
    } else {
      setPointsToRedeem(appliedPoints ?? 0);
    }
  };

  const handleRedeemClick = () => {
    setAccordionValue("fluid-points");
  };

  const handleApply = () => {
    if (onApplyPoints) {
      onApplyPoints(pointsToRedeem, useForEntireSubtotal);
    }
  };

  const handleRemovePoints = () => {
    if (onRemovePoints) {
      onRemovePoints();
      setPointsToRedeem(0);
      setUseForEntireSubtotal(false);
    }
  };

  const handlePointsChange = (value: number) => {
    let newValue: number;
    if (value < 0 || isNaN(value)) {
      newValue = 0;
    } else if (maxApplicablePoints && value > maxApplicablePoints) {
      newValue = maxApplicablePoints;
    } else {
      newValue = value;
    }

    setPointsToRedeem(newValue);

    if (
      useForEntireSubtotal &&
      maxApplicablePoints &&
      newValue !== maxApplicablePoints
    ) {
      setUseForEntireSubtotal(false);
    }
  };

  const incrementPoints = () => {
    handlePointsChange(pointsToRedeem + 1);
  };

  const decrementPoints = () => {
    handlePointsChange(pointsToRedeem - 1);
  };

  return (
    <div className="w-full border-b border-gray-300 pb-4">
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={setAccordionValue}
        className="w-full"
      >
        <AccordionItem value="fluid-points" className="border-b-0">
          <div className="flex flex-row items-center justify-between">
            <div className="mt-4 mb-1 text-sm text-gray-400">
              {t("points_available", undefined, {
                label: capitalizeFirst(pluralPointsLabel(company)),
              })}
            </div>
            <AccordionTrigger className="m-0 ml-2 flex h-auto min-h-0 w-auto py-0 hover:no-underline" />
          </div>

          <div className="text-sm font-medium text-gray-900">
            {customerTotalPoints} (
            {customerTotalPointsInCurrency ?? `${currencySymbol}0`})
            {appliedPoints && appliedPoints > 0 ? (
              <span>
                {" "}
                - {appliedPoints}{" "}
                {t("applied_points", undefined, {
                  label: pointsLabel(appliedPoints ?? 0, company),
                })}
              </span>
            ) : null}
          </div>

          {!accordionValue && (
            <button
              onClick={handleRedeemClick}
              className="mt-2 cursor-pointer text-sm text-gray-900 hover:text-gray-700"
            >
              +{" "}
              {t("redeem_points", undefined, {
                label: pluralPointsLabel(company),
              })}
            </button>
          )}

          <AccordionContent className="pt-2">
            <div className="mt-2 space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="use-points-entire-subtotal"
                  checked={useForEntireSubtotal}
                  onChange={(e) => handleCheckboxChange(e.target.checked)}
                  className="h-4 w-4 cursor-pointer rounded border-gray-300 text-black focus:ring-black"
                />
                <label
                  htmlFor="use-points-entire-subtotal"
                  className="ml-3 cursor-pointer text-sm font-medium text-gray-700"
                >
                  {t("use_all_applicable_points", undefined, {
                    labelPlural: pluralPointsLabel(company),
                    points: maxApplicablePoints ?? 0,
                    label: pointsLabel(maxApplicablePoints ?? 0, company),
                  })}
                </label>

                {appliedPoints && appliedPoints > 0 ? (
                  <button
                    onClick={handleRemovePoints}
                    disabled={isRemoving}
                    className="ml-auto cursor-pointer text-sm text-gray-900 underline hover:text-gray-700"
                  >
                    {isRemoving ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-2 border-gray-400 border-t-transparent"></div>
                    ) : (
                      t("remove_points", undefined, {
                        label: pluralPointsLabel(company),
                      })
                    )}
                  </button>
                ) : null}
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleApply();
                }}
                className="flex flex-row items-center gap-2"
              >
                <div className="relative flex flex-1 items-center">
                  <Input
                    type="number"
                    min={0}
                    max={customerTotalPoints}
                    value={pointsToRedeem || ""}
                    placeholder="0"
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      handlePointsChange(value);
                    }}
                    className="h-input w-full rounded border border-gray-300 px-3 pr-8 text-base [-moz-appearance:textfield] focus:border-none focus:ring-2 focus:ring-black focus:outline-hidden focus:ring-inset sm:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                  <div className="absolute right-1 z-10 flex flex-col gap-0">
                    <button
                      type="button"
                      onClick={incrementPoints}
                      disabled={pointsToRedeem >= (customerTotalPoints ?? 0)}
                      className={cn(
                        "flex h-5 w-5 items-center justify-center rounded-t border border-gray-300 text-gray-600 hover:text-gray-900",
                        pointsToRedeem &&
                          customerTotalPoints &&
                          pointsToRedeem >= customerTotalPoints &&
                          "cursor-not-allowed opacity-50",
                      )}
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={decrementPoints}
                      disabled={pointsToRedeem <= 0}
                      className={cn(
                        "flex h-5 w-5 items-center justify-center rounded-b border border-t-0 border-gray-300 text-gray-600 hover:text-gray-900",
                        pointsToRedeem <= 0 && "cursor-not-allowed opacity-50",
                      )}
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={
                    isLoading ||
                    pointsToRedeem <= 0 ||
                    customerTotalPoints === 0
                  }
                  className={cn(
                    "h-input bg-brand-primary text-brand-primary-text rounded px-6 font-semibold",
                    isLoading && "cursor-not-allowed opacity-75",
                  )}
                >
                  {isLoading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent"></div>
                  ) : (
                    t("apply")
                  )}
                </Button>
              </form>

              {appliedPoints && appliedPoints > 0 ? (
                <div className="space-y-1">
                  <div className="text-sm text-gray-500">
                    {t("using_points", undefined, {
                      points: appliedPoints ?? 0,
                      label: pointsLabel(appliedPoints ?? 0, company),
                    })}{" "}
                    {appliedPointsAmountInCurrency &&
                      `(${appliedPointsAmountInCurrency})`}
                  </div>
                </div>
              ) : null}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
